import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Compass } from 'lucide-react';
import { Button } from '../components/ui/button';

const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO = "'IBM Plex Mono', monospace";
const REDIRECT_SECONDS = 10;

const NotFound = () => {
  const navigate = useNavigate();
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(timer.current);
          navigate('/', { replace: true });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timer.current);
  }, [paused, navigate]);

  const progress = ((REDIRECT_SECONDS - seconds) / REDIRECT_SECONDS) * 100;

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-20 bg-white" data-testid="not-found-page">
      <div className="max-w-xl w-full text-center">
        <motion.p
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="text-[hsl(var(--primary))] mb-6" style={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.25em' }}
        >
          ✦ ERROR 404 · PAGE NOT FOUND
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
          className="text-7xl sm:text-8xl font-semibold text-foreground tracking-tight mb-4"
          style={{ fontFamily: SERIF }}
        >
          404
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
          className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-10 max-w-md mx-auto"
        >
          This page has wandered off the runway. The link may be broken or the page may have moved.
        </motion.p>

        {/* Soft-redirect countdown */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
          className="border border-border p-6 mb-8"
          data-testid="not-found-countdown-card"
        >
          {paused ? (
            <p className="text-sm text-muted-foreground" data-testid="not-found-countdown-paused">
              Auto-redirect paused. Take your time.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Redirecting you to the homepage in{' '}
              <span className="font-semibold text-foreground" style={{ fontFamily: MONO }} data-testid="not-found-countdown">
                {seconds}s
              </span>
            </p>
          )}
          {/* progress bar */}
          <div className="mt-4 h-[3px] w-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-[hsl(var(--primary))] transition-all duration-1000 ease-linear"
              style={{ width: paused ? '0%' : `${progress}%` }}
            />
          </div>
          {!paused && (
            <button
              onClick={() => setPaused(true)}
              className="mt-4 text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
              style={{ fontFamily: MONO }}
              data-testid="not-found-cancel-redirect"
            >
              Cancel redirect
            </button>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24 }}
          className="flex flex-col sm:flex-row gap-3 justify-center"
        >
          <Button
            onClick={() => navigate('/')}
            className="rounded-none bg-foreground text-background hover:bg-foreground/85 gap-2 px-6 h-11"
            data-testid="not-found-home-btn"
          >
            <Home className="w-4 h-4" /> Back to Home
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="rounded-none border-border hover:border-foreground gap-2 px-6 h-11"
            data-testid="not-found-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Go Back
          </Button>
          <Link to="/gallery">
            <Button
              variant="ghost"
              className="rounded-none hover:bg-secondary gap-2 px-6 h-11 w-full sm:w-auto text-[hsl(var(--primary))]"
              data-testid="not-found-gallery-btn"
            >
              <Compass className="w-4 h-4" /> Explore Talent
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;
